import { Typography, MenuItem, Grid, Box } from "@material-ui/core";
import { useFormikContext } from "formik";

import { TicketFormValues } from "../../../interface/formik/TicketFormValues";
import { TruckTypes } from "../../../utils/constants";
import FormErrorMessage from "../../shared/form-error-message";
import FormSelect from "../../shared/form-select";
import FormTextField from "../../shared/form-text-field";
import FormTruckLicensePlateField from "../../shared/form-truck-license-plate-field";
import PhoneInput from "../../shared/phone-input";
import FormSelectSearchField from "../../shared/form-select-search-field";

import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  getTransportCompanies,
  getTransportCompanyTrucks,
  setTransportCompanyContacts,
  setTransportCompanyTrucks,
} from "../../../store/transportStore";

import { toast } from "react-toastify";
import transportContactService from "../../../services/transportContactService";
import truckService from "../../../services/truckService";

import { TransportPayload } from "../../../interface/api/TransportPayload";
import { SetTransportContactsAction } from "../../../interface/redux-types/SetTransportContactsAction";
import { SetTrucksAction } from "../../../interface/redux-types/SetTrucksAction";
import { TransportContact } from "../../../interface/api/TransportContact";
import { Truck } from "../../../interface/api/Truck";

const TransportInformation = () => {
  const dispatch = useAppDispatch();
  const transportCompanies = useAppSelector(getTransportCompanies);
  const trucks = useAppSelector(getTransportCompanyTrucks);
  const { values, setFieldValue } = useFormikContext<TicketFormValues>();

  const updateTransportCompanyTrucks = async (transportCompanyId: number) => {
    const payload: TransportPayload = {
      transportCompanyId,
      truck: null,
      transportContact: null,
    };

    const response = await truckService.getTrucks(payload);
    if (response.ok) {
      dispatch(setTransportCompanyTrucks({ trucks: response.data as Truck[] }));
    } else {
      toast.error(response.originalError.message);
    }
  };

  const updateTransportCompanyContacts = async (transportCompanyId: number) => {
    const payload: TransportPayload = {
      transportCompanyId,
      truck: null,
      transportContact: null,
    };

    const response = await transportContactService.getTransportContacts(
      payload
    );
    if (response.ok) {
      dispatch(
        setTransportCompanyContacts({
          transportContacts: response.data as TransportContact[],
        })
      );
    } else {
      toast.error(response.originalError.message);
    }
  };

  const onTransportCompanyChange = (selectedOption: any) => {
    const transportCompanyId = selectedOption?.value || 0;
    setFieldValue("transportCompanyId", transportCompanyId);

    if (!transportCompanyId) {
      setFieldValue("transportCompanyName", "");
      setFieldValue("truckId", 0);
      setFieldValue("truckDriverName", "");
      setFieldValue("truckDriverNumber", "");
      setFieldValue("truckLicensePlate", "");
      return;
    }

    const selectedCompany = transportCompanies.find(
      (tc) => tc.id === transportCompanyId
    );
    setFieldValue("transportCompanyName", selectedCompany?.name || "");

    updateTransportCompanyTrucks(transportCompanyId);
    updateTransportCompanyContacts(transportCompanyId);
  };

  const onTruckChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const truckId = Number(e.target.value);
    setFieldValue("truckId", truckId);

    const selectedTruck = trucks.find((t) => t.id === truckId);
    if (selectedTruck) {
      setFieldValue("truckDriverName", selectedTruck.driverName || "");
      setFieldValue("truckDriverNumber", selectedTruck.driverNumber || "");
      setFieldValue("truckLicensePlate", selectedTruck.licensePlate || "");
    } else {
      // Only clear if fields are already empty (user cleared selection)
      if (
        !values.truckDriverName &&
        !values.truckDriverNumber &&
        !values.truckLicensePlate
      ) {
        setFieldValue("truckDriverName", "");
        setFieldValue("truckDriverNumber", "");
        setFieldValue("truckLicensePlate", "");
      }
    }
  };

  return (
    <Box sx={{ marginBottom: "16px" }}>
      <Typography
        variant="h6"
        style={{ fontWeight: 600, textDecoration: "underline", marginBottom: 12 }}
      >
        TRANSPORT
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormSelectSearchField
            name="transportCompanyId"
            placeholder="Select Haulage Co."
            value={
              transportCompanies
                .map((c) => ({ label: c.name, value: c.id }))
                .find((opt) => opt.value === values.transportCompanyId) || 0
            }
            options={transportCompanies.map((c) => ({
              label: c.name,
              value: c.id,
            }))}
            onChange={onTransportCompanyChange}
            isClearable
          />
          <FormErrorMessage name="transportCompanyId" />
        </Grid>

        <Grid item xs={6}>
          <FormSelect
            name="truckId"
            label="Truck Number"
            value={values.truckId}
            items={[
              trucks.map((truck) => (
                <MenuItem key={truck.id} value={truck.id}>
                  {truck.number}
                </MenuItem>
              )),
            ]}
            onChange={onTruckChange}
          />
        </Grid>

        <Grid item xs={6}>
          <FormTruckLicensePlateField
            name="truckLicensePlate"
            label="Truck License Plate"
            value={values.truckLicensePlate || ""}
            placeholder={null}
          />
          <FormErrorMessage name="truckLicensePlate" />
        </Grid>

        <Grid item xs={6}>
          <FormTextField
            name="truckDriverName"
            label="Truck Driver Name"
            value={values.truckDriverName}
          />
          <FormErrorMessage name="truckDriverName" />
        </Grid>

        <Grid item xs={6}>
          <PhoneInput
            name="truckDriverNumber"
            label="Truck Driver Number"
            value={values.truckDriverNumber || ""}
          />
          <FormErrorMessage name="truckDriverNumber" />
        </Grid>

        <Grid item xs={12}>
          <FormSelect
            name="truckTypeId"
            label="Triaxle or Trailer"
            value={values.truckTypeId}
            items={[
              TruckTypes.map((type, index) => (
                <MenuItem key={index} value={type.value}>
                  {type.label}
                </MenuItem>
              )),
            ]}
          />
          <FormErrorMessage name="truckTypeId" />
        </Grid>
      </Grid>
    </Box>
  );
};

export default TransportInformation;
